// Google's own sign-in button (Google Identity Services). The redirect flow
// through Supabase shows the Supabase project's domain on Google's consent
// screen; this button signs in on this site, so the screen names it. The
// script loads only when the button is drawn.

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface GisIdApi {
  initialize(config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    nonce?: string;
    use_fedcm_for_button?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type: 'standard';
      theme: 'outline' | 'filled_black';
      size: 'medium' | 'large';
      text: 'continue_with';
      shape: 'rectangular' | 'pill';
      width?: number;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GisIdApi } };
  }
}

let loading: Promise<GisIdApi> | null = null;

/** Loads the script once per session. A failure (offline, blocked) is
 * forgotten so the next draw tries again. */
export function loadGoogleIdentity(): Promise<GisIdApi> {
  const ready = window.google?.accounts?.id;
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise<GisIdApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => {
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error('Google sign-in did not load.'));
    };
    script.onerror = () => reject(new Error('Google sign-in did not load.'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

/** A fresh nonce: the raw value goes to Supabase, its SHA-256 (hex) to
 * Google, which writes it into the ID token for Supabase to check. */
export async function makeNonce(): Promise<{ raw: string; hashed: string }> {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  const raw = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return { raw, hashed };
}

let initialised: Promise<GisIdApi> | null = null;

/** Initialises Google's library once per page: it keeps only the LAST
 * initialize, and the drawer and the sign-in card both draw a button, so
 * one shared config (and one nonce, matching the token it produces) serves
 * every button. `onCredential` receives the ID token and the raw nonce. */
export function initGoogleIdentity(
  clientId: string,
  onCredential: (token: string, nonce: string) => void,
): Promise<GisIdApi> {
  if (initialised) return initialised;
  initialised = (async () => {
    const nonce = await makeNonce();
    const api = await loadGoogleIdentity();
    api.initialize({
      client_id: clientId,
      nonce: nonce.hashed,
      use_fedcm_for_button: true,
      callback: (response) => {
        if (response.credential) onCredential(response.credential, nonce.raw);
      },
    });
    return api;
  })().catch((error: unknown) => {
    initialised = null;
    throw error;
  });
  return initialised;
}

export function __resetGoogleIdentityForTests(): void {
  initialised = null;
  loading = null;
}
